<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-if="textFileContent != null"
    class="white-space-pre json-viewer FMFilePreviewJSON w-100 m-1"
    :class="{
      'overflow-hidden': overflowHidden,
      'recently-change': recentlyChanged,
      'preview-image-shadow': !bigShadow,
      'preview-image-shadow-big': bigShadow,
    }"
    v-html="textFileContent"
  />
  <ProgressSpinner v-else />
</template>

<script setup lang="ts">
import ProgressSpinner from "primevue/progressspinner";
import { ref, watch } from "vue";
import { getMinioSignedUrl } from "@/functions/useMinioSignedUrl";

const props = defineProps({
  fileUUID: {
    type: String,
    required: true,
  },
  mimeType: {
    type: String,
    required: true,
  },
  overflowHidden: {
    type: Boolean,
    required: false,
    default: false,
  },
  recentlyChanged: {
    type: Boolean,
    required: false,
    default: false,
  },
  bigShadow: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const textFileContent = ref<string>();

function escapeHtml(s: string) {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// Minimal JSON highlighter
function highlightJson(prettyJson: string) {
  const safe = escapeHtml(prettyJson);

  return safe.replace(
    /(&quot;(?:\\.|[^\\])*?&quot;)(\s*:)?|\b(true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g,
    (match, quotedString, isKey, booleanValue) => {
      if (quotedString != null) {
        if (isKey != null) {
          return `<span class="json-key">${quotedString}</span>${isKey}`;
        }
        return `<span class="json-string">${quotedString}</span>`;
      }

      if (booleanValue != null) {
        return `<span class="json-boolean">${match}</span>`;
      }

      if (match === "null") {
        return `<span class="json-null">${match}</span>`;
      }

      return `<span class="json-number">${match}</span>`;
    },
  );
}

watch(
  () => props.fileUUID,
  async (fileUUID) => {
    textFileContent.value = undefined;

    if (!fileUUID) return;

    const controller = new AbortController();
    const mySignal = controller.signal;

    // If selection changes again, abort previous fetch
    const stop = watch(
      () => props.fileUUID,
      (newUUID) => {
        if (newUUID !== fileUUID) controller.abort();
      },
      { flush: "sync" },
    );

    try {
      const url = await getMinioSignedUrl(fileUUID);
      if (url == null || props.fileUUID !== fileUUID) {
        return;
      }

      const res = await fetch(url, { method: "GET", signal: mySignal });
      if (!res.ok) return;

      const raw = await res.text();

      // If entity changed meanwhile, ignore
      if (props.fileUUID !== fileUUID) {
        return;
      }

      if (props.mimeType === "application/json") {
        // Pretty-print + highlight
        try {
          const pretty = JSON.stringify(JSON.parse(raw), null, 2);
          textFileContent.value = highlightJson(pretty);
        } catch {
          // Fallback: show raw text if JSON parse fails
          textFileContent.value = escapeHtml(raw);
        }
      } else {
        textFileContent.value = escapeHtml(raw);
      }
    } catch {
      // ignore abort errors
    } finally {
      stop();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
@use "@/modules/filemanagement/components/FMFileViewer/FMFileViewer.scss" as *;

.json-viewer {
  background: var(--p-black);
  color: var(--p-gray-200);
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre;
}
</style>

<style lang="scss">
.FMFilePreviewJSON {
  /* highlight colors */
  .json-key {
    color: var(--p-indigo-300) !important;
  }
  .json-string {
    color: var(--p-emerald-300);
  }
  .json-number {
    color: var(--p-rose-300);
  }
  .json-boolean {
    color: var(--p-purple-300);
  }
  .json-null {
    color: var(--p-orange-500);
  }
}
</style>
